<template lang="pug" src="./option-editor.pug"></template>
<style scoped lang="scss" src="./option-editor.scss"></style>

<script>
import { mapState, mapGetters } from 'vuex';
import { validationMixin } from 'vuelidate';
import { required, numeric } from 'vuelidate/lib/validators';
import { demical } from '@/utils/helpers';
import validate from '@/mixins/validate';

import request from '@/utils/request';

export default {
    mixins: [validationMixin, validate],
    data: () => ({
        option: {
            optionName: null,
            optionClassId: null,
            districtId: null,
            description: null,
            startPrice: null,
            priceForKm: null,
            priceForWaiting: null,
            minPrice: null,
            multiplier: 1,
            multiplierForDriver: 1,
            defaultOptions: false,
            internal: false,
            hidden: false,
            brandId: null,
            levels: [],
            numberOfSits: null
        },
        defaultData: {},
        activeLevel: 0,
        canSaveOption: false,
        canRemoveLevel: true,
        canAddLevel: true,
        loaded: false,
        removeModal: false,
        requestProcessing: false
    }),
    validations: {
        option: {
            optionName: {
                required
            },
            startPrice: {
                required,
                demical
            },
            priceForKm: {
                required,
                demical
            },
            priceForWaiting: {
                required,
                demical
            },
            minPrice: {
                required,
                numeric
            },
            multiplier: {
                required,
                demical
            },
            multiplierForDriver: {
                required,
                demical
            },
            brandId: {
                required
            }
        }
    },
    computed: {
        ...mapGetters(['userBrandId']),
        ...mapState({
            brands: state => state.data.brands,
            districts: state => state.data.districts,
            optionClasses: state => state.data.optionClasses
        }),
        saveMethod() {
            return this.option.id ? 'put' : 'post';
        },
        saveUrl() {
            return this.option.id ? `/admin/options/${this.option.id}` : '/admin/options';
        },
        activeNoDefaultLevel() {
            return this.activeLevel != 0;
        }
    },
    watch: {
        option: {
            handler: function() {
                this.canSaveOption = true;
            },
            deep: true
        }
    },
    created() {
        if (this.userBrandId) {
            this.option.brandId = this.userBrandId;
        }

        if (this.$route.params.id) {
            this.loaded = true;
            this.loadData();
        }

        this.$store.dispatch('data/getBrands');
        this.$store.dispatch('data/getDistricts');
        this.$store.dispatch('data/getOptionClasses');
    },
    methods: {
        async loadData() {
            await request({ method: 'get', url: `/admin/options/${this.$route.params.id}` })
                .then(res => this.successLoad(res)).catch(error => {
                    if(error.response && error.response.data && error.response.data.error) {
                        this.$store.dispatch('alert/addMessage', {
                            message: error.response.data.error
                        });
                    }
                });
        },

        setActiveLevel(level, data, ignore = false) {
            if (this.canSaveOption) return;

            if (!Object.keys(this.defaultData).length && level != 0) {
                this.defaultData = Object.assign({}, this.option);
            }

            if (level != 0 && !ignore) {
                this.option = Object.assign({}, this.option, {
                    startPrice: data.startPrice,
                    priceForKm: data.priceForKm,
                    priceForWaiting: data.priceForWaiting,
                    minPrice: data.minPrice,
                    optionName: data.optionName
                });
            } else {
                this.option = Object.assign({}, this.option, this.defaultData, { levels: this.option.levels });
                this.defaultData = {};
            }

            this.activeLevel = level;

            this.$nextTick(() => {
                this.canSaveOption = false;
            });
        },

        async saveOption() {
            if (this.$v.option.$invalid) {
                this.$v.option.$touch();

                this.$nextTick(() => this.focusFirstErrorInput());
                return;
            }

            if (this.requestProcessing) return;

            this.requestProcessing = true;

            const data = Object.assign({}, this.option, {
                level: this.activeLevel
            });

            await request({ method: this.saveMethod, url: this.saveUrl, data })
                .then(res => {
                    this.successLoad(res, this.activeLevel, data);

                    if (!this.$route.params.id) {
                        this.$router.push(`/finances/tariffs/editor/${this.option.id}`);
                    }

                    this.$store.dispatch('alert/addMessage', {
                        type: 'success',
                        message: 'success'
                    });
                }).catch(error => {
                    if(error.response && error.response.data && error.response.data.error) {
                        this.$store.dispatch('alert/addMessage', {
                            message: error.response.data.error
                        });
                    }
                }).finally(() => {
                    this.$nextTick(() => {
                        this.requestProcessing = false;
                        this.canSaveOption = false;
                    });
                });
        },

        successLoad(res, level = 0, data) {
            let additionalData = {};

            if (level != 0 && data) {
                const { minPrice, startPrice, priceForKm, priceForWaiting, optionName } = data;

                additionalData = { minPrice, startPrice, priceForKm, priceForWaiting, optionName };
                this.$set(this.option.levels, level - 1, Object.assign({}, this.option.levels[level - 1], additionalData));
            }

            this.option = Object.assign({}, this.option, res.data, additionalData);

            if (!this.option.defaultOptions && this.option.levels.length) {
                this.$set(this.option, 'levels', []);
            }

            this.$nextTick(() => {
                this.canSaveOption = false;
                this.loaded = false;
            });
        },

        async remove() {
            if (this.requestProcessing) return;

            this.requestProcessing = true;

            await request({ method: 'delete', url: `/admin/options/${this.option.id}` })
                .then(() => {
                    this.$router.push(`/finances/tariffs`);
                }).catch(error => {
                    if(error.response && error.response.data && error.response.data.error) {
                        this.$store.dispatch('alert/addMessage', {
                            message: error.response.data.error
                        });
                    }
                }).finally(() => {
                    this.$nextTick(() => {
                        this.requestProcessing = false;
                        this.removeModal = false;
                    });
                });
        },

        async addLevel() {
            if (this.requestProcessing) return;

            this.requestProcessing = true;
            this.canAddLevel = false;
            this.canRemoveLevel = false;

            await request({ method: 'post', url: `/admin/options/${this.option.id}/option-level` })
                .then(() => {
                    const option = this.activeLevel != 0 ? this.defaultData : this.option;
                    const { startPrice, priceForKm, priceForWaiting, minPrice, optionName } = option;

                    this.option.levels.push({
                        level: this.option.levels.length + 1,
                        startPrice, priceForKm,
                        priceForWaiting, minPrice,
                        optionName
                    });

                    this.$store.dispatch('alert/addMessage', {
                        message: 'success',
                        type: 'success'
                    });
                }).catch(error => {
                    if(error.response && error.response.data && error.response.data.error) {
                        this.$store.dispatch('alert/addMessage', {
                            message: error.response.data.error
                        });
                    }
                }).finally(() => {
                    this.$nextTick(() => {
                        this.requestProcessing = false;
                        this.canSaveOption = false;

                        setTimeout(() => {
                            this.canAddLevel = true;
                            this.canRemoveLevel = true;
                        }, 1000);
                    });
                });
        },

        async removeLevel() {
            if (this.requestProcessing) return;

            this.requestProcessing = true;
            this.canAddLevel = false;
            this.canRemoveLevel = false;

            await request({ method: 'delete', url: `/admin/options/${this.option.id}/option-level` })
                .then(() => {
                    this.setActiveLevel(0, null, true);
                    this.option.levels.pop();

                    this.$store.dispatch('alert/addMessage', {
                        message: 'success',
                        type: 'success'
                    });
                }).catch(error => {
                    if(error.response && error.response.data && error.response.data.error) {
                        this.$store.dispatch('alert/addMessage', {
                            message: error.response.data.error
                        });
                    }
                }).finally(() => {
                    this.$nextTick(() => {
                        this.requestProcessing = false;
                        this.canSaveOption = false;

                        setTimeout(() => {
                            this.canAddLevel = true;
                            this.canRemoveLevel = true;
                        }, 1000);
                    });
                });
        }
    }
};
</script>
